/** design §5 quota table. */
export const MIN_FREE_BYTES_TO_CAPTURE = 15 * 1024 * 1024; // 15 MB
export const WARN_USAGE_RATIO = 0.8; // 80%
export const WARN_PENDING_COUNT = 30;
export const WARN_OLDEST_AGE_MS = 4 * 60 * 60 * 1000; // 4h

export interface StorageEstimateResult {
  usage: number;
  quota: number;
}

/**
 * getStorageEstimate — thin wrapper over `navigator.storage.estimate()`
 * (task 4.5). Returns null when the Storage Manager API is unsupported
 * (older Safari) rather than throwing — callers (shouldBlockCapture,
 * shouldWarnUsage) treat "unknown" as "fail open", never as "block".
 */
export async function getStorageEstimate(
  storage: StorageManager | undefined,
): Promise<StorageEstimateResult | null> {
  if (!storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await storage.estimate();
    return { usage, quota };
  } catch {
    // MEDIUM fix (adversarial review) — some iOS/private-browsing contexts
    // reject estimate() outright. Treated exactly like "API unsupported":
    // fail open, never let a rejected estimate propagate past this wrapper.
    return null;
  }
}

/**
 * shouldBlockCapture — true when free space (quota - usage) is below the
 * 15MB floor. Fails open (false) when the estimate is unavailable — an
 * unsupported browser must never be permanently blocked from capturing.
 *
 * MEDIUM fix (adversarial review) — also fails open when `quota` is
 * missing/0/negative/non-finite. Some iOS/private-browsing contexts
 * resolve `estimate()` to `{}` (getStorageEstimate defaults that to
 * `{usage:0, quota:0}`), which — WITHOUT this guard — makes
 * `free = quota - usage` evaluate to `0` (or negative), permanently
 * below the 15MB floor: capture would stay blocked forever on such a
 * device, even online, even with plenty of ACTUAL free space. `quota <= 0`
 * (or non-finite) is treated the same as "no estimate at all" —
 * `shouldWarnUsage` right below already encodes the same
 * `quota <= 0` → "don't trust this" rule; this brings the two predicates
 * back in sync.
 */
export function shouldBlockCapture(estimate: StorageEstimateResult | null): boolean {
  if (!estimate) return false;
  if (!Number.isFinite(estimate.quota) || estimate.quota <= 0) return false;
  const free = estimate.quota - estimate.usage;
  return free < MIN_FREE_BYTES_TO_CAPTURE;
}

/** shouldWarnUsage — true at/above 80% of quota used. */
export function shouldWarnUsage(estimate: StorageEstimateResult | null): boolean {
  if (!estimate || estimate.quota <= 0) return false;
  return estimate.usage / estimate.quota >= WARN_USAGE_RATIO;
}

/** shouldWarnPendingCount — true when more than 30 items are queued. */
export function shouldWarnPendingCount(pendingCount: number): boolean {
  return pendingCount > WARN_PENDING_COUNT;
}

/** shouldWarnOldestAge — true when the oldest pending item is older than 4h. */
export function shouldWarnOldestAge(
  oldestCapturedAt: Date | null,
  now: Date = new Date(),
): boolean {
  if (!oldestCapturedAt) return false;
  return now.getTime() - oldestCapturedAt.getTime() > WARN_OLDEST_AGE_MS;
}
